import { Box, CircularProgress, Dialog, DialogContent, DialogContentText, DialogTitle, Stack, Typography } from '@mui/material';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { useSnackbar } from 'notistack';
import { useQueryClient } from 'react-query';
import { auth } from 'apis';
import { HANDLE_ROLE_SELECTION, LOGIN, SET_CREDS, STORE_MENU } from 'store/actions';
import RoleCard from './RoleCard';
import './RoleSelectionDialog.css';

const getRoleValue = (item) => item?.value || item?.Value;

const RoleSelectionDialog = (props) => {
    const { ...rest } = props;
    const { t } = useTranslation();
    const [disabledButtons, setDisabledButtons] = useState(false);
    const selectionInProgressRef = useRef(false);
    const { openRoleSelection, roles, creds } = useSelector((state) => state.role);
    const { urpfId } = useSelector((state) => state.auth);
    const dispatch = useDispatch();
    const { enqueueSnackbar } = useSnackbar();
    const navigate = useNavigate();
    const queryClient = useQueryClient();

    const onClose = () => {
        if (selectionInProgressRef.current) return;

        dispatch({ type: HANDLE_ROLE_SELECTION, open: false });
        setDisabledButtons(false);
    };

    const completeAuth = async (res) => {
        dispatch({ type: STORE_MENU, menuItems: res?.menu });
        dispatch({ type: LOGIN, payload: res });
        dispatch({ type: SET_CREDS, creds: null });
        await queryClient.removeQueries('dashboard');
        await queryClient.invalidateQueries('me');
        await queryClient.invalidateQueries('user-functions');
    };

    const handleSelectRole = async (item) => {
        if (selectionInProgressRef.current) return;

        selectionInProgressRef.current = true;
        setDisabledButtons(true);
        try {
            const selectedUrpfId = getRoleValue(item);
            const res = creds
                ? await auth.login({
                      username: creds?.email,
                      password: creds?.password,
                      selectedUrpfId
                  })
                : await auth.switchFunction(selectedUrpfId);

            await completeAuth(res);
            enqueueSnackbar(creds ? t('LoginSuccess', 'Login successful') : t('RoleSwitchSuccessful', 'Role switch successful'), {
                variant: 'success'
            });
            dispatch({ type: HANDLE_ROLE_SELECTION, open: false });
            selectionInProgressRef.current = false;
            setDisabledButtons(false);
            navigate('/home');
        } catch (error) {
            enqueueSnackbar(error?.data?.message || error?.message || t('SomethingWentWrong', 'Something went wrong'), {
                variant: 'error'
            });
            selectionInProgressRef.current = false;
            setDisabledButtons(false);
        }
    };

    return (
        <Dialog open={openRoleSelection ?? false} onClose={onClose} {...rest} maxWidth="lg" fullWidth>
            <DialogTitle>
                <Typography variant="h3" sx={{ flexGrow: 1 }}>
                    {t('SelectRole', 'Select Role')}
                </Typography>
            </DialogTitle>
            {disabledButtons ? (
                <DialogContent sx={{ display: 'flex', minHeight: 300, justifyContent: 'center', alignItems: 'center' }}>
                    <CircularProgress />
                </DialogContent>
            ) : (
                <DialogContent sx={{ bgcolor: '#fefefe' }}>
                    <Stack direction="row" alignItems="center" justifyContent="flex-start" spacing={4} sx={{ mb: 0 }}>
                        <DialogContentText>
                            <Box
                                sx={{
                                    p: 1,
                                    whiteSpace: 'pre-wrap',
                                    overflowWrap: 'break-word'
                                }}
                            >
                                {t('SelectRolesDescription', 'Select the role you want to continue with.')}
                            </Box>
                        </DialogContentText>
                    </Stack>
                    <div className="role-card-grid">
                        {roles?.map((item, index) => {
                            const value = getRoleValue(item);
                            return (
                                <RoleCard
                                    key={value || index}
                                    data={item}
                                    isSelected={item?.selected || item?.Selected || value === urpfId}
                                    isDisabled={disabledButtons}
                                    handleSelectRole={() => handleSelectRole(item)}
                                />
                            );
                        })}
                    </div>
                </DialogContent>
            )}
        </Dialog>
    );
};

export default RoleSelectionDialog;
